// LAS PESTAÑAS — la primera contesta, las otras cuatro muestran de dónde sale.
//
// La estructura es una jerarquía de dos niveles, y la diferencia entre ellos no
// es de tamaño sino de PREGUNTA:
//
//   Cómo va      ¿cómo voy? ¿hay algo que revisar? ¿llego al mes?
//                Se lee en diez segundos. Es la pestaña activa al entrar, así
//                que quien abre y cierra se lleva la respuesta sin buscar nada.
//
//   Las otras    "quiero ver los números de tickets de julio"
//                Ahí sí conviene separar por tema, porque quien llega ya sabe
//                qué está buscando.
//
// El diseño original decía "nada de pestañas: un ancla indexa, una pestaña
// oculta". Ese argumento es correcto CONTRA LAS PESTAÑAS ACTUALES, que están
// ordenadas por tipo de dato y dejan la conclusión escondida en la cuarta
// solapa. Con la conclusión en la PRIMERA y activa por defecto, separar el
// detalle por tema deja de esconder y pasa a ordenar.
//
// Cada pestaña de detalle mezcla las dos cosas: arriba el bloque que INTERPRETA
// —titular, frase, la lectura escrita— y debajo la pestaña de siempre con sus
// gráficos y tablas, reusada sin tocar una línea. Nada se pierde.
//
// ⚠️ AL IMPRIMIR SE MUESTRAN TODAS. Una pestaña que no está montada no se puede
// imprimir, y el papel para el contador es uno de los motivos de esta pantalla.

import React, { useState, useEffect, useMemo } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { UI } from "./theme";

// Nivel 1 — la respuesta
import PulsoDeHoy from "./PulsoDeHoy";
import ParaMirar from "./ParaMirar";
import LoQueSaltaALaVista from "./LoQueSaltaALaVista";
import ElMes from "./ElMes";

// Los bloques que interpretan
import DeDondeVieneLaPlata from "./DeDondeVieneLaPlata";
import ComoTePagan from "./ComoTePagan";
import TusDias from "./TusDias";
import CrecesOSubistePrecios from "./CrecesOSubistePrecios";
import ParaElContador from "./ParaElContador";
import CurvaDelDia from "./CurvaDelDia";
import CuantoSeQueda from "./CuantoSeQueda";
import TuTecho from "./TuTecho";

// El dato duro: las pestañas de siempre, reusadas sin tocar una línea.
import TabIngresos from "../Stats/TabIngresos";
import TabTickets from "../Stats/TabTickets";
import TabMensual from "../Stats/TabMensual";
import TabAsistente from "../Stats/TabAsistente";
import PeriodComparison from "../Stats/PeriodComparison";
import PeriodSelector from "../Stats/PeriodSelector";
import KpiCards from "../Stats/KpiCards";

const PESTANAS = [
  { id: "como-va", label: "Cómo va", icono: "insights" },
  { id: "ingresos", label: "Ingresos", icono: "payments" },
  { id: "tickets", label: "Tickets", icono: "confirmation_number" },
  { id: "mensual", label: "Mes a mes", icono: "calendar_month" },
  { id: "ocupacion", label: "Ocupación", icono: "local_parking" },
  { id: "preguntar", label: "Preguntar", icono: "smart_toy" },
];

// El selector no se muestra donde no gobierna nada. Decir "últimos 7 días"
// arriba de un bloque que mira 90 sería peor que no tener selector.
const CON_PERIODO = new Set(["ingresos", "tickets"]);

/**
 * ANCHO DE LA PESTAÑA "CÓMO VA".
 *
 * Es la pestaña de LEER: bloques de texto, frases y números sueltos. En un
 * monitor de 2.500 px una tarjeta de tres renglones se ve vacía aunque el texto
 * esté acotado, porque el fondo blanco sigue corriendo de lado a lado.
 *
 * Se limita la columna entera y se centra, en vez de partirla en dos. Dos
 * columnas romperían lo único que ordena esta pantalla —la lectura de arriba
 * hacia abajo por urgencia: hoy, lo que hay que revisar, el mes, los patrones—
 * porque al ponerlas al lado deja de estar claro qué se lee primero. Y las
 * tarjetas no son pares: una tiene un gráfico y otra tres líneas, así que
 * emparejarlas deja una mitad vacía.
 *
 * Las otras pestañas NO se limitan: ahí hay gráficos y tablas, que sí
 * aprovechan cada píxel de ancho.
 */
const ANCHO_LECTURA_TAB = 1400;

/** Tope de días que se le pasan a las pestañas de siempre. Coincide con la
 *  opción más larga del selector (1 año): si fuera menor, esa opción mentiría. */
const TOPE_DIAS = 365;

const Titulo = ({ children }) => (
  <h4
    style={{
      fontSize: 13, fontWeight: 700, color: UI.textoSuave,
      textTransform: "uppercase", letterSpacing: 0.6, margin: "22px 0 12px",
    }}
  >
    {children}
  </h4>
);

const PestanasDetalle = ({
  cerrados, hoy, vivo, mensuales, diarios, capacidad, abonados, companyId, nombre,
}) => {
  const [activa, setActiva] = useState("como-va");
  const [dias, setDias] = useState(30);
  const [imprimiendo, setImprimiendo] = useState(false);

  useEffect(() => {
    const antes = () => setImprimiendo(true);
    const despues = () => setImprimiendo(false);
    window.addEventListener("beforeprint", antes);
    window.addEventListener("afterprint", despues);
    return () => {
      window.removeEventListener("beforeprint", antes);
      window.removeEventListener("afterprint", despues);
    };
  }, []);

  // Los componentes de Stats/ se escribieron contra `dailyDocs` DESCENDENTE y
  // cortado a 90 (Stats/index.js:111-112), y varios hacen `slice(0, 30)` desde
  // el principio. Se les pasa exactamente esa forma: darles los 395 ascendentes
  // les daría "los 30 días más viejos" en silencio — que es justo el bug que ya
  // apareció una vez en esta pantalla.
  // 365 y no 90. El selector ofrece hasta "1 año", y con el tope viejo elegir
  // 6 meses o 1 año devolvía 90 días IGUAL, sin avisar: las pestañas de siempre
  // hacen `slice(0, period)` sobre lo que se les pasa, así que el recorte se
  // comía la opción en silencio. Ofrecer un período que no se puede cumplir es
  // peor que no ofrecerlo.
  //
  // Es seguro: ningún componente de Stats/ recorre el arreglo entero — todos
  // cortan por período o miran `.length`. Verificado uno por uno.
  const dailyDocs = useMemo(
    () => [...(diarios || [])]
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
      .slice(0, TOPE_DIAS),
    [diarios]
  );

  // Hasta dónde puede llegar el selector, en DÍAS DE CALENDARIO.
  //
  // Antes era `cerrados.length`, o sea la cantidad de DOCUMENTOS: a un cliente
  // que abre 200 de los últimos 365 días le deshabilitaba "1 año" aunque tenga
  // un año entero de historia. Lo que limita no es cuántos días operó, sino
  // desde cuándo hay datos — y el tope de lo que efectivamente se carga.
  const maxDias = useMemo(() => {
    if (!cerrados.length) return 0;
    const primero = new Date(`${cerrados[0].date}T00:00:00`).getTime();
    const span = Math.round((Date.now() - primero) / 86400000);
    return Math.max(0, Math.min(span, TOPE_DIAS));
  }, [cerrados]);
  const monthlyDocs = useMemo(
    () => [...(mensuales || [])]
      .sort((a, b) => String(b.month || "").localeCompare(String(a.month || ""))),
    [mensuales]
  );

  // La ventana para los bloques que SÍ dependen del período. Los que tienen
  // ventana fija por diseño —Tus días mira 90, el contador el mes cerrado,
  // ¿creces? es año contra año— reciben `cerrados` entero: recortarlos los
  // dejaría en su estado vacío sin que el dueño entienda por qué.
  const cerradosPeriodo = useMemo(
    () => cerrados.slice(-dias),
    [cerrados, dias]
  );

  const ver = (id) => imprimiendo || activa === id;

  const contenido = {
    "como-va": (
      <div style={{ maxWidth: ANCHO_LECTURA_TAB }}>
        {/* LA TIRA DE KPIs, ARRIBA DE TODO.
            Estaba en la pestaña "Resumen" de la pantalla de siempre, que es la
            única que esta pantalla no reusa, y se estaba perdiendo. Su valor no
            es cada número por separado —varios los contestan mejor los bloques
            de abajo— sino EL VISTAZO DE DOS SEGUNDOS, que ningún bloque da.
            Y trae dos cosas que no estaban en ningún otro lado: los tickets del
            período, y la tasa de anulación cuando es estable (Para mirar sólo
            avisa cuando se sale de lo normal, así que una playa que anula un
            7% todos los meses nunca lo veía). */}
        <div className="mb-3">
          {/* Sin "Ocupacion" ni "Hoy": los dice mejor el bloque de abajo, que
              ademas agrega cuantos lugares quedan y como viene contra un dia
              normal. Esta tira queda como el vistazo del PERIODO. */}
          <KpiCards dailyDocs={dailyDocs} liveData={vivo} ocultar={["ocupacion", "hoy"]} />
        </div>

        {/* Los cuatro son <Bloque> y ya traen su propio margen inferior: un
            wrapper con mt-3 acá duplicaba el espacio entre los dos primeros. */}
        <PulsoDeHoy hoy={hoy} cerrados={cerrados} vivo={vivo} capacidad={capacidad} />
        <ParaMirar cerrados={cerrados} abonados={abonados} />
        {/* Va acá y no al final: son números sueltos que se leen de una, así que
            entran después de lo que hay que revisar y antes de la única cuenta
            de esta pestaña que pide leerse despacio, que es el cierre del mes.
            Recibe `hoy` y `mensuales` porque uno de sus renglones es el cierre
            del mes, y lo pide con la MISMA llamada que hace `ElMes` acá abajo:
            los dos tienen que mostrar el mismo rango o la pestaña se desmiente
            sola. */}
        <LoQueSaltaALaVista cerrados={cerrados} capacidad={capacidad} />
        <ElMes cerrados={cerrados} hoy={hoy} mensuales={mensuales} companyId={companyId} />
      </div>
    ),
    ingresos: (
      <>
        <DeDondeVieneLaPlata cerrados={cerradosPeriodo} mensuales={mensuales} />
        <ComoTePagan cerrados={cerradosPeriodo} mensuales={mensuales} />
        <Titulo>Los números</Titulo>
        <TabIngresos dailyDocs={dailyDocs} liveData={vivo} periodo={dias} />
      </>
    ),
    tickets: (
      <>
        <TusDias cerrados={cerrados} />
        <Titulo>Los números</Titulo>
        <TabTickets dailyDocs={dailyDocs} periodo={dias} />
      </>
    ),
    mensual: (
      <>
        {/* EL ORDEN ES POR ACTUALIDAD, de lo que cambia todos los días a lo que
            cambia una vez al año. "¿Creces o subiste los precios?" compara
            contra el mismo mes del año pasado: es la conclusión más valiosa de
            esta pestaña, pero también la que se mueve más lento — arriba de
            todo ocupaba el mejor lugar para decir algo que sigue igual que la
            semana pasada. */}

        {/* Compara día / semana / mes / año contra su período anterior, y es lo
            más fresco que hay acá: la columna "Hoy" se mueve sola. Ningún
            bloque nuevo la cubre — "El mes" sólo llega hasta el mes. */}
        <Titulo>Contra el período anterior</Titulo>
        <PeriodComparison dailyDocs={dailyDocs} monthlyDocs={monthlyDocs} liveData={vivo} />

        <ParaElContador cerrados={cerrados} mensuales={mensuales} nombre={nombre} />

        <CrecesOSubistePrecios mensuales={mensuales} />
        <Titulo>Los números</Titulo>
        {/* `sinProyeccion`: la pestaña de siempre trae su propia tarjeta "Si
            sigues a este ritmo, así termina el mes", que proyecta con una regla
            de tres y da un monto exacto. "El mes", en la primera pestaña, da un
            rango calibrado con el error histórico del cliente y concluye si
            termina arriba o abajo del mes pasado. Los dos números nunca
            coinciden, y el badge de crecimiento de la tarjeta puede quedar en
            verde mientras "El mes" dice que va a terminar abajo. Igual que con
            el selector de período: cuando conviven, manda el de la pantalla
            nueva. */}
        <TabMensual monthlyDocs={monthlyDocs} dailyDocs={dailyDocs} sinProyeccion />
      </>
    ),
    // La única sin "los números" debajo: la ocupación por hora y la permanencia
    // no existían hasta ahora, así que no hay gráfico viejo que ponerle al pie.
    ocupacion: (
      <>
        <CurvaDelDia cerrados={cerrados} hoy={hoy} capacidad={capacidad} />
        <CuantoSeQueda cerrados={cerradosPeriodo} />
        <TuTecho cerrados={cerrados} vivo={vivo} capacidad={capacidad} />
      </>
    ),
    preguntar: (
      <TabAsistente
        dailyDocs={dailyDocs}
        monthlyDocs={monthlyDocs}
        liveData={vivo}
        companyId={companyId}
      />
    ),
  };

  return (
    <>
      <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 stats2-pestanas">
        <Nav tabs className="flex-grow-1">
          {PESTANAS.map((p) => (
            <NavItem key={p.id}>
              <NavLink
                className={activa === p.id ? "active" : ""}
                onClick={() => setActiva(p.id)}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>
                  {p.icono}
                </span>
                {p.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {/* Un solo selector para toda la pestaña. Las de siempre traían el suyo
            propio y ahora lo esconden cuando les llega éste: dos selectores
            marcando períodos distintos en la misma vista es la incoherencia que
            esta pantalla existe para sacar. */}
        {CON_PERIODO.has(activa) && (
          <div className="mb-2">
            <PeriodSelector value={dias} onChange={setDias} maxDays={maxDias} />
          </div>
        )}
      </div>

      <TabContent activeTab={imprimiendo ? "__todas__" : activa} className="mt-3">
        {PESTANAS.map((p) => (
          <TabPane key={p.id} tabId={p.id} className={imprimiendo ? "d-block" : undefined}>
            {/* Montaje diferido: sin esto las seis pestañas calcularían y
                dibujarían todos sus gráficos al entrar, aunque se mire una. */}
            {ver(p.id) && (
              <div id={`pestana-${p.id}`}>
                {imprimiendo && <Titulo>{p.label}</Titulo>}
                {contenido[p.id]}
              </div>
            )}
          </TabPane>
        ))}
      </TabContent>
    </>
  );
};

export default PestanasDetalle;
